import { useTranslation } from 'react-i18next'
import { Button, WarningIcon20 } from '@repo/ui'
import { BlockIcon24, DoneIcon32 } from '@repo/ui/icons'
import { Abruftoken } from '@/models/Abruftoken'
import { useDialog } from '@/components/common/dialog'
import BfnQuittierenDialog from '@/features/befehlsnachrichten/components/dialogs/BfnQuittierenDialog'
import BfnAbweisenDialog from '@/features/befehlsnachrichten/components/dialogs/BfnAbweisenDialog'
import BefehleAlleGelesenContext from '@/features/befehlsnachrichten/hooks/useBefehleAlleGelesenContext'
import { useContext } from 'react'
import useOnlineDetection from '@/features/offline/hooks/useOnlineDetection'

export interface BfnAbgerufenActionsProps {
  abruftoken: Abruftoken
  'data-testid': string
}

export default function BfnAbgerufenActions({
  abruftoken,
  'data-testid': testId
}: Readonly<BfnAbgerufenActionsProps>) {
  const { t } = useTranslation('bfnDetails')
  const [isQuittierenDialogOpen, openQuittierenDialog, closeQuittierenDialog] =
    useDialog()
  const [isAbweisenDialogOpen, openAbweisenDialog, closeAbweisenDialog] =
    useDialog()
  const { befehleAlleGelesen } = useContext(BefehleAlleGelesenContext)
  const isOnline = useOnlineDetection()

  return (
    <>
      <BfnQuittierenDialog
        abruftoken={abruftoken}
        state={isQuittierenDialogOpen}
        onClose={closeQuittierenDialog}
        data-testid={`${testId}-quittieren-dialog`}
      />
      <BfnAbweisenDialog
        abruftoken={abruftoken}
        state={isAbweisenDialogOpen}
        onClose={closeAbweisenDialog}
        data-testid={`${testId}-abweisen-dialog`}
      />

      {befehleAlleGelesen && (
        <div className="flex gap-0.5">
          <div>
            <DoneIcon32
              className="shrink-0"
              data-testid={`${testId}-all-read-icon`}
            />
          </div>
          <div data-testid={`${testId}-all-read-text`}>
            {t('gelesen_erforderlich_info.success')}
          </div>
        </div>
      )}

      {!befehleAlleGelesen && (
        <div className="flex gap-0.5">
          <div>
            <WarningIcon20
              className="shrink-0"
              data-testid={`${testId}-not-all-read-icon`}
            />
          </div>
          <div data-testid={`${testId}-not-all-read-text`}>
            {t('gelesen_erforderlich_info.required')}
          </div>
        </div>
      )}

      <Button
        variant="brand-primary"
        className="w-full"
        disabled={!befehleAlleGelesen || !isOnline}
        onClick={() => openQuittierenDialog()}
        data-testid={`${testId}-accept-standort-button`}
      >
        <DoneIcon32
          className="shrink-0"
          data-testid={`${testId}-accept-standort-button-icon`}
        />
        <span
          className="pl-0.5"
          data-testid={`${testId}-accept-standort-button-text`}
        >
          {t('actions.quittieren_button')}
        </span>
      </Button>

      <Button
        variant="secondary-solid"
        className="w-full"
        disabled={!isOnline}
        onClick={() => openAbweisenDialog()}
        data-testid={`${testId}-deny-standort-button`}
      >
        <BlockIcon24
          className="shrink-0"
          data-testid={`${testId}-deny-standort-button-icon`}
        />
        <span
          className="pl-0.5"
          data-testid={`${testId}-deny-standort-button-text`}
        >
          {' '}
          {t('actions.abweisen_button')}
        </span>
      </Button>
    </>
  )
}
